import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getApiBaseUrl,
  getXSRFToken,
  login,
} from '../../orval/custom-instance';
import type {
  Citation,
  OnboardingChatTurn,
  Profile,
  SignedTranscript,
  StatusPhase,
  TurnCost,
} from './types';
import { computeBreakdown, type PhaseMark, type TimingBreakdown } from './turnTiming';

/** Client → server request body. Only the two channels the user controls. */
export type SendBody = {
  signedTranscript: SignedTranscript | null;
  userMessage: string;
  profileEdits?: Partial<Profile>;
};

/** The final `done` payload. */
export type DonePayload = {
  turn: OnboardingChatTurn;
  signedTranscript: SignedTranscript;
  /** Sources the research used (best-effort; may be empty). */
  citations?: Citation[];
  cost?: TurnCost;
  timing?: TimingBreakdown;
};

export type StreamState = {
  isStreaming: boolean;
  phase: StatusPhase;
  partialContent: string;
  /** Actual web-search query strings, surfaced live and cycled by the UI. */
  queries: string[];
  /** URLs the search has consulted so far, surfaced live for favicon chips. */
  sources: Citation[];
};

const INITIAL_STATE: StreamState = {
  isStreaming: false,
  phase: 'reasoning',
  partialContent: '',
  queries: [],
  sources: [],
};

export type SendCallbacks = {
  onDone: (done: DonePayload) => void;
  onError: (message: string) => void;
};

export type ParsedEvent = { event: string; data: string };

export const parseFrames = (buffer: string): { events: ParsedEvent[]; rest: string } => {
  const events: ParsedEvent[] = [];
  // Normalise CRLF → LF so the frame delimiter is uniform even if a proxy or
  // CDN in front of the API rewrites line endings. Mirrors the backend reader.
  let rest = buffer.replace(/\r\n/g, '\n');
  let sepIndex = rest.indexOf('\n\n');
  while (sepIndex !== -1) {
    const frame = rest.slice(0, sepIndex);
    rest = rest.slice(sepIndex + 2);

    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
    if (dataLines.length > 0) {
      events.push({ event: eventName, data: dataLines.join('\n') });
    }
    sepIndex = rest.indexOf('\n\n');
  }
  return { events, rest };
};

/**
 * Parse a `Retry-After` header into a whole number of seconds. The header may
 * be either a number of seconds (the form Laravel's throttle middleware sends)
 * or an HTTP-date. Returns null when absent or unparseable.
 */
export const parseRetryAfter = (headerValue: string | null): number | null => {
  // Trim first so a whitespace-only header (which Number() would coerce to 0)
  // is treated as absent rather than an explicit 0-second retry.
  const value = headerValue?.trim();
  if (!value) return null;
  const asSeconds = Number(value);
  if (Number.isFinite(asSeconds)) return Math.max(0, Math.ceil(asSeconds));
  const asDate = Date.parse(value);
  if (!Number.isNaN(asDate)) {
    return Math.max(0, Math.ceil((asDate - Date.now()) / 1000));
  }
  return null;
};

/** Friendly, non-technical message for a 429 rate-limit response. */
export const rateLimitMessage = (retrySeconds: number | null): string => {
  if (retrySeconds && retrySeconds > 0) {
    const minutes = Math.ceil(retrySeconds / 60);
    const wait =
      retrySeconds >= 60
        ? `${minutes} minute${minutes === 1 ? '' : 's'}`
        : `${retrySeconds} second${retrySeconds === 1 ? '' : 's'}`;
    return `You're sending messages too quickly. Please wait ${wait} and try again.`;
  }
  return "You're sending messages too quickly. Please wait a moment and try again.";
};

export const useStreamingOnboardingChat = () => {
  const [state, setState] = useState<StreamState>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (body: SendBody, { onDone, onError }: SendCallbacks) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState({ isStreaming: true, phase: 'reasoning', partialContent: '', queries: [], sources: [] });

      // Client-measured phase timeline for the dev debug badge. t0 = send.
      const t0 = performance.now();
      const marks: PhaseMark[] = [{ phase: 'reasoning', at: t0 }];
      let firstTokenAt: number | null = null;
      const markPhase = (phase: StatusPhase) => {
        if (marks[marks.length - 1]?.phase !== phase) {
          marks.push({ phase, at: performance.now() });
        }
      };

      let response: Response;
      try {
        // login() / getXSRFToken() can reject (e.g. the CSRF cookie fetch
        // failing); without wrapping them the hook would stay pinned to
        // isStreaming: true with no onError ever firing.
        const authToken = await login();
        const xsrfToken = await getXSRFToken();
        const baseUrl = getApiBaseUrl();

        response = await fetch(`${baseUrl}/onboarding-chat/completion-stream`, {
          method: 'POST',
          // Listing application/json first makes Laravel's expectsJson() return
          // true, so a failed-auth response is a 401 JSON instead of a redirect
          // to the login route (which then trips CORS for our origin).
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/event-stream',
            'X-Requested-With': 'XMLHttpRequest',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            ...(xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {}),
          },
          credentials: 'include',
          redirect: 'error',
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } catch (err) {
        setState(INITIAL_STATE);
        // An abort (Stop button / unmount) discards the partial turn silently.
        if (!controller.signal.aborted) {
          onError(err instanceof Error ? err.message : 'Request failed');
        }
        return;
      }

      if (!response.ok || !response.body) {
        setState(INITIAL_STATE);
        if (response.status === 429) {
          const retrySeconds = parseRetryAfter(response.headers.get('Retry-After'));
          onError(rateLimitMessage(retrySeconds));
        } else {
          onError(`Stream request failed with HTTP ${response.status}`);
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const { events, rest } = parseFrames(buffer);
          buffer = rest;

          for (const evt of events) {
            let payload: unknown;
            try {
              payload = JSON.parse(evt.data);
            } catch (e) {
              console.warn('[onboarding-stream] failed to parse SSE data', e, evt.data);
              continue;
            }
            const data = (payload ?? {}) as Record<string, unknown>;
            if (import.meta.env.DEV) {
              console.debug('[onboarding-stream] event', evt.event, data);
            }

            switch (evt.event) {
              case 'status': {
                const phase = data.phase as StatusPhase | undefined;
                const queries = Array.isArray(data.queries)
                  ? (data.queries as string[])
                  : undefined;
                // Cumulative consulted-source list; each frame replaces the last.
                const sources = Array.isArray(data.sources)
                  ? (data.sources as Citation[])
                  : undefined;
                if (phase) markPhase(phase);
                setState((prev) => ({
                  ...prev,
                  ...(phase ? { phase } : {}),
                  ...(queries ? { queries } : {}),
                  ...(sources ? { sources } : {}),
                }));
                break;
              }
              case 'delta': {
                const text = (data.text as string | undefined) ?? '';
                if (text) {
                  if (firstTokenAt === null) firstTokenAt = performance.now();
                  markPhase('writing');
                  setState((prev) => ({
                    ...prev,
                    phase: 'writing',
                    partialContent: prev.partialContent + text,
                  }));
                }
                break;
              }
              case 'done': {
                completed = true;
                setState(INITIAL_STATE);
                onDone({
                  turn: data.turn as OnboardingChatTurn,
                  signedTranscript: data.signedTranscript as SignedTranscript,
                  citations: data.citations as Citation[] | undefined,
                  cost: data.cost as TurnCost | undefined,
                  timing: computeBreakdown(marks, t0, firstTokenAt),
                });
                break;
              }
              case 'error': {
                completed = true;
                setState(INITIAL_STATE);
                onError((data.message as string | undefined) ?? 'Stream error');
                break;
              }
            }
            if (completed) break;
          }
          if (completed) break;
        }
      } catch (err) {
        setState(INITIAL_STATE);
        if (!controller.signal.aborted) {
          onError(err instanceof Error ? err.message : 'Stream read failed');
        }
        return;
      }

      if (!completed && !controller.signal.aborted) {
        setState(INITIAL_STATE);
        onError('Stream ended without completion');
      }
    },
    [],
  );

  /**
   * Interrupt the in-flight turn: abort the stream, discard the partial reply,
   * and leave the profile/transcript unchanged (no onDone fires).
   */
  const stop = useCallback(() => {
    abortRef.current?.abort();
    setState(INITIAL_STATE);
  }, []);

  return { state, send, stop };
};
